import * as yaml from 'js-yaml';
import { 
  Playbook, 
  PlaybookTransformItem, 
  PlaybookEventConfig, 
  PlaybookEnrichment,
  PlaybookMapping,
  PlaybookExpression,
  PlaybookSpreading,
  PlaybookFilter, // Import PlaybookFilter
  SchemaComparisonResult,
  OutputProperty, // Import OutputProperty
  PlaybookParamValue
} from '../types/playbook';
import { UnifiedSchema, Parameter, SchemaType } from '../types/schema';
import { toFriendlyName } from './schemaUtils';
import { v4 as uuidv4 } from 'uuid';

/*
 * To run tests after modifying YAML export code:
 *
 * 1. Run Playbook Utility Tests:
 *    npm test src/utils/playbookUtils.test.ts
 *
 * 2. Run Connection Export Tests:
 *    npm test src/utils/connectionExport/connectionExport.test.ts
 *
 * 3. Run Clean YAML Export Tests (newly added for validation):
 *    npm test src/utils/cleanYamlExport.test.ts
 *
 * 4. Run All Utility Tests (comprehensive check):
 *    npm test src/utils/
 *
 * 5. Run TypeScript Compilation Check:
 *    npx tsc --noEmit
 */

// Import type for UI state
interface PlaybookEditState {
  playbookId: string;
  name: string;
  description?: string;
  type: 'pipeline' | 'integration';
  version: string;
  params: PlaybookParamValue[];
  input_schema_id?: string;
  output_schema_id?: string;
  uiTransforms: PlaybookTransformItem[];
  uiEventConfigs: PlaybookEventConfig[];
  autoPromoteGlobals?: boolean;
  allowUndefinedTransforms?: boolean;
  connectionJSON?: any;
}

/**
 * Generate a unique ID for transforms and other entities
 */
function generateId(): string {
  return uuidv4().substring(0, 8);
}

/**
 * Convert UI state to a proper Playbook model for export
 * This is the unified function used by all export paths
 */
export function convertUiStateToPlaybook(uiState: PlaybookEditState): Playbook {
  const playbook: Playbook = {
    id: uiState.playbookId,
    name: uiState.name,
    description: uiState.description || '',
    type: uiState.type,
    version: uiState.version,
    params: uiState.params || [],
    input_schema_id: uiState.input_schema_id,
    output_schema_id: uiState.output_schema_id,
    content: {}, // Will be populated from transforms
    status: 'saved',
    uiTransforms: uiState.uiTransforms,
    uiEventConfigs: uiState.uiEventConfigs,
    autoPromoteGlobals: uiState.autoPromoteGlobals,
    allowUndefinedTransforms: uiState.allowUndefinedTransforms,
    connectionJSON: uiState.connectionJSON
  };

  return playbook;
}

/**
 * Unified export function for both playbook and connection YAML
 * This ensures all export paths produce identical results
 */
export function exportPlaybookAndConnectionYaml(uiState: PlaybookEditState): {
  playbookYaml: string;
  connectionYaml: string;
} {
  // Convert UI state to proper Playbook model
  const playbook = convertUiStateToPlaybook(uiState);
  
  // Generate clean YAML using the unified export functions
  const playbookYaml = dumpPlaybookToYaml(playbook);
  const connectionYaml = dumpConnectionToYaml(playbook);
  
  return {
    playbookYaml,
    connectionYaml
  };
}

/**
 * Parse YAML content into a Playbook UI model
 * Enhanced to preserve original YAML structure
 */
export function parsePlaybookFromYaml(yamlContent: string): Playbook & { __originalYaml?: any } {
  try {
    const yamlData = yaml.load(yamlContent) as any;
    
    // Store the original YAML structure for later use when exporting
    const originalStructure = structuredClone(yamlData);
    
    // Extract basic metadata
    const playbook: Playbook & { __originalYaml?: any } = {
      id: '', // Will be set when saving
      name: '', // Will be set by user
      description: '',
      type: 'pipeline', // Default type
      version: yamlData.version || '1.0.0',
      params: yamlData.params || [],
      input_schema_id: yamlData.input_schema_id, // Read from YAML
      output_schema_id: yamlData.output_schema_id, // Read from YAML
      content: yamlData.content || {}, // Initialize content, will be populated below
      status: 'saved',
      uiTransforms: [],
      uiEventConfigs: [],
      autoPromoteGlobals: true, // Default value
      allowUndefinedTransforms: yamlData.allowUndefinedTransforms ?? false, // Read from YAML, default to false
      // Store the original YAML structure
      __originalYaml: originalStructure
    };

    // Check if uiTransforms, uiEventConfigs, and autoPromoteGlobals are directly in content
    if (yamlData.content && Array.isArray(yamlData.content.uiTransforms)) {
      playbook.uiTransforms = yamlData.content.uiTransforms;
    }
    if (yamlData.content && Array.isArray(yamlData.content.uiEventConfigs)) {
      playbook.uiEventConfigs = yamlData.content.uiEventConfigs;
    }
    if (yamlData.content && yamlData.content.autoPromoteGlobals !== undefined) {
      playbook.autoPromoteGlobals = yamlData.content.autoPromoteGlobals;
    }
    if (yamlData.content && yamlData.content.connectionJSON !== undefined) {
      playbook.connectionJSON = yamlData.content.connectionJSON;
    }

    // If uiTransforms or uiEventConfigs were not found in content,
    // or if the YAML is an older format, parse from top-level sections.
    // This ensures backward compatibility.
    if (playbook.uiTransforms.length === 0 && playbook.uiEventConfigs.length === 0) {
      // Parse global transforms
      if (yamlData.global) {
        const globalTransforms = parseTransformsFromYamlSection(yamlData.global, 'global');
        globalTransforms.forEach((transform, index) => {
          transform.__yamlMetadata = {
            section: 'global',
            type: transform.type,
            index
          };
          transform.mappingType = 'yaml-imported';
        });
        playbook.uiTransforms.push(...globalTransforms);
      }

      // Parse event-specific transforms
      if (yamlData.eventSpecific) {
        const transformMap = new Map<string, PlaybookTransformItem>();
        const eventConfigs: Record<string, PlaybookEventConfig> = {};

        Object.entries(yamlData.eventSpecific).forEach(([eventName, eventData]: [string, any]) => {
          const eventId = generateId();
          eventConfigs[eventId] = {
            id: eventId,
            name: toFriendlyName(eventName),
            codeName: eventName,
            description: '',
            transformItemIds: []
          };

          if (eventData) {
            ['enrichments', 'mappings', 'expressions', 'spreadings', 'filters'].forEach((transformType) => {
              if (Array.isArray(eventData[transformType])) {
                eventData[transformType].forEach((transformData: any, itemIndex: number) => {
                  const signature = createTransformSignature(transformType, transformData);
                  if (transformMap.has(signature)) {
                    const existingTransform = transformMap.get(signature)!;
                    if (!existingTransform.eventIds) existingTransform.eventIds = [];
                    if (!existingTransform.eventIds.includes(eventId)) {
                      existingTransform.eventIds.push(eventId);
                    }
                    eventConfigs[eventId].transformItemIds?.push(existingTransform.id);
                  } else {
                    const newTransforms = parseTransformsFromYamlSection(
                      { [transformType]: [transformData] },
                      'eventSpecific',
                      [eventId]
                    );
                    if (newTransforms.length > 0) {
                      const newTransform = newTransforms[0];
                      newTransform.__yamlMetadata = {
                        section: 'eventSpecific',
                        type: transformType.replace(/s$/, ''),
                        index: itemIndex,
                        eventName
                      };
                      newTransform.mappingType = 'yaml-imported';
                      transformMap.set(signature, newTransform);
                      if (!eventConfigs[eventId].transformItemIds) eventConfigs[eventId].transformItemIds = [];
                      eventConfigs[eventId].transformItemIds.push(newTransform.id);
                    }
                  }
                });
              }
            });
          }
        });
        playbook.uiTransforms.push(...transformMap.values());
        playbook.uiEventConfigs.push(...Object.values(eventConfigs));
      }

      // Parse default transforms
      if (yamlData.default) {
        const defaultEventId = generateId();
        const defaultEventConfig: PlaybookEventConfig = {
          id: defaultEventId,
          name: 'Default',
          codeName: 'default',
          description: 'Default event configuration',
          transformItemIds: []
        };
        const transformMap = new Map<string, PlaybookTransformItem>();

        if (yamlData.default) {
          ['enrichments', 'mappings', 'expressions', 'spreadings', 'filters'].forEach((transformType) => {
            if (Array.isArray(yamlData.default[transformType])) {
              yamlData.default[transformType].forEach((transformData: any, itemIndex: number) => {
                const signature = createTransformSignature(transformType, transformData);
                let existingTransform = playbook.uiTransforms.find(t =>
                  t.appliesToScope === 'eventSpecific' &&
                  createTransformSignature(t.type, t.content) === signature
                );

                if (existingTransform) {
                  if (!existingTransform.eventIds) existingTransform.eventIds = [];
                  if (!existingTransform.eventIds.includes(defaultEventId)) {
                    existingTransform.eventIds.push(defaultEventId);
                  }
                  defaultEventConfig.transformItemIds?.push(existingTransform.id);
                } else if (transformMap.has(signature)) {
                  existingTransform = transformMap.get(signature)!;
                  defaultEventConfig.transformItemIds?.push(existingTransform.id);
                } else {
                  const newTransforms = parseTransformsFromYamlSection(
                    { [transformType]: [transformData] },
                    'eventSpecific',
                    [defaultEventId]
                  );
                  if (newTransforms.length > 0) {
                    const newTransform = newTransforms[0];
                    newTransform.__yamlMetadata = {
                      section: 'default',
                      type: transformType.replace(/s$/, ''),
                      index: itemIndex
                    };
                    newTransform.mappingType = 'yaml-imported';
                    transformMap.set(signature, newTransform);
                    defaultEventConfig.transformItemIds?.push(newTransform.id);
                  }
                }
              });
            }
          });
          playbook.uiTransforms.push(...transformMap.values());
        }
        playbook.uiEventConfigs.push(defaultEventConfig);
      }
    }

    return playbook;
  } catch (error) {
    console.error('Error parsing YAML:', error);
    throw new Error(`Failed to parse playbook YAML: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

/**
 * Convert Playbook UI model to clean v1.0 compatible YAML
 * This produces YAML that conforms to the v1.0 specification without UI metadata
 */
export function dumpPlaybookToYaml(playbook: Playbook & { __originalYaml?: any }): string {
  try {
    const yamlData: any = {};

    // Copy top-level properties from original YAML that are part of the v1.0 spec
    if (playbook.__originalYaml) {
      const v1SpecKeys = new Set([
        'version', 'params', 'global', 'eventSpecific', 'default',
        // Include other v1.0 spec fields but exclude UI metadata
        'name', 'description', 'type'
      ]);
      Object.keys(playbook.__originalYaml).forEach(key => {
        // Only include fields that are part of the v1.0 specification
        if (v1SpecKeys.has(key)) {
          yamlData[key] = structuredClone(playbook.__originalYaml[key]);
        }
      });
    }
    
    // Always include these core v1.0 spec properties from the playbook UI model
    yamlData.version = playbook.version;
    yamlData.params = playbook.params;
    
    // DO NOT include UI metadata fields in v1.0 YAML:
    // - input_schema_id (UI metadata)
    // - output_schema_id (UI metadata) 
    // - allowUndefinedTransforms (UI configuration)
    // - content.connectionJSON (UI state)

    // Separate global and event-specific transforms
    const globalTransforms = playbook.uiTransforms.filter(t => t.appliesToScope === 'global');
    const eventSpecificTransforms = playbook.uiTransforms.filter(t => t.appliesToScope === 'eventSpecific');

    // Add global section if there are global transforms
    if (globalTransforms.length > 0) {
      yamlData.global = buildYamlSection(globalTransforms);
    } else if (yamlData.global) {
      // If global section exists from original YAML but no global transforms, clear it
      delete yamlData.global;
    }

    // Group event-specific transforms by event
    const transformsByEvent = new Map<string, PlaybookTransformItem[]>();
    eventSpecificTransforms.forEach(transform => {
      if (transform.eventIds && transform.eventIds.length > 0) {
        transform.eventIds.forEach(eventId => {
          const eventConfig = playbook.uiEventConfigs.find(e => e.id === eventId);
          const eventName = eventConfig?.codeName || eventId;
          
          if (!transformsByEvent.has(eventName)) {
            transformsByEvent.set(eventName, []);
          }
          transformsByEvent.get(eventName)!.push(transform);
        });
      }
    });

    // Build event-specific sections
    if (transformsByEvent.size > 0) {
      yamlData.eventSpecific = {};
      transformsByEvent.forEach((transforms, eventName) => {
        if (eventName === 'default') {
          // Handle default transforms separately
          yamlData.default = buildYamlSection(transforms);
        } else {
          yamlData.eventSpecific[eventName] = buildYamlSection(transforms);
        }
      });
      // If eventSpecific object is empty after processing, remove it
      if (Object.keys(yamlData.eventSpecific).length === 0) {
        delete yamlData.eventSpecific;
      }
    } else if (yamlData.eventSpecific) {
      // If eventSpecific section exists from original YAML but no event-specific transforms, clear it
      delete yamlData.eventSpecific;
    }

    // Handle default section explicitly if it wasn't handled as part of eventSpecific
    const defaultEventConfig = playbook.uiEventConfigs.find(e => e.codeName === 'default');
    const defaultTransforms = defaultEventConfig 
      ? playbook.uiTransforms.filter(t => t.eventIds?.includes(defaultEventConfig.id))
      : [];

    if (defaultTransforms.length > 0) {
      yamlData.default = buildYamlSection(defaultTransforms);
    } else if (yamlData.default) {
      // If default section exists from original YAML but no default transforms, clear it
      delete yamlData.default;
    }

    // DO NOT include connectionJSON in playbook YAML - it's UI state, not part of v1.0 spec
    // Remove any content section as it's not part of the v1.0 playbook specification
    if (yamlData.content) {
      delete yamlData.content;
    }

    return yaml.dump(yamlData, { 
      indent: 2,
      lineWidth: -1,
      noRefs: true,
      sortKeys: false
    });
  } catch (error) {
    console.error('Error dumping YAML:', error);
    throw new Error(`Failed to convert playbook to YAML: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

/**
 * Export connection YAML (clean v1.0 format for connections)
 * Connections should not include UI metadata fields like input_schema_id/output_schema_id
 */
export function dumpConnectionToYaml(playbook: Playbook): string {
  try {
    const connectionData: any = {};

    // Only include v1.0 connection spec fields, exclude UI metadata
    if (playbook.connectionJSON) {
      connectionData.params = playbook.connectionJSON.params;
      connectionData.default = playbook.connectionJSON.default;
      if (playbook.connectionJSON.eventSpecific) {
        connectionData.eventSpecific = playbook.connectionJSON.eventSpecific;
      }
    }
    
    // DO NOT include UI metadata fields in connection YAML:
    // - name (not part of connection spec)
    // - description (not part of connection spec) 
    // - version (not part of connection spec)
    // - type (not part of connection spec)
    // - input_schema_id (UI metadata)
    // - output_schema_id (UI metadata)

    return yaml.dump(connectionData, {
      indent: 2,
      lineWidth: -1,
      noRefs: true,
      sortKeys: false
    });
  } catch (error) {
    console.error('Error dumping connection YAML:', error);
    throw new Error(`Failed to export connection YAML: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

/**
 * Parse YAML content into a simplified connection model.
 * This is used for importing connection.yaml files.
 */
export function parseConnectionFromYaml(parsedYamlData: any): Playbook { // Adjusted to accept parsed data directly
  try {
    const playbook: Playbook = {
      id: '', // Will be set when saving
      name: parsedYamlData.name || '', // Populate name from YAML
      description: parsedYamlData.description || '', // Populate description from YAML
      type: parsedYamlData.type || 'connection', // Default to 'connection'
      version: parsedYamlData.version || '1.0.0', // Default version
      params: parsedYamlData.params || [],
      input_schema_id: parsedYamlData.input_schema_id,
      output_schema_id: parsedYamlData.output_schema_id,
      content: {
        global: {}, // Connections don't typically have global transforms in the same way playbooks do
        default: parsedYamlData.default || {}, // Direct mapping of default section
        eventSpecific: parsedYamlData.eventSpecific || {}, // Direct mapping of eventSpecific section
      },
      status: 'saved',
      uiTransforms: [], // Connections don't use uiTransforms in the same way playbooks do
      uiEventConfigs: [], // Connections don't use uiEventConfigs in the same way playbooks do
      autoPromoteGlobals: false, // Not applicable for connections
      __originalYaml: structuredClone(parsedYamlData) // Store original YAML
    };

    return playbook;
  } catch (error) {
    console.error('Error parsing connection YAML:', error);
    throw new Error(`Failed to parse connection YAML: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

/**
 * Generate derived output schema based on playbook transforms
 */
export function deriveOutputSchema(playbook: Playbook, inputSchema: UnifiedSchema): UnifiedSchema {
  try {
    const derivedParameters: Parameter[] = [];
    const processedPaths = new Set<string>();

    // Process each transform to derive output properties
    playbook.uiTransforms.forEach(transform => {
      switch (transform.type) {
        case 'enrichment':
          const enrichment = transform.content as PlaybookEnrichment;
          if (enrichment.outputKey === undefined) {
            throw new Error('Enrichment outputKey cannot be undefined for schema derivation.');
          }
          if (!processedPaths.has(enrichment.outputKey)) {
            derivedParameters.push(createDerivedParameter(enrichment.outputKey, 'string', 'Enriched value'));
            processedPaths.add(enrichment.outputKey);
          }
          break;

        case 'mapping':
          const mapping = transform.content as PlaybookMapping;
          if (mapping.outputKey === undefined) {
            throw new Error('Mapping outputKey cannot be undefined for schema derivation.');
          }
          if (mapping.inputKey === undefined) {
            throw new Error('Mapping inputKey cannot be undefined for schema derivation.');
          }
          if (!processedPaths.has(mapping.outputKey)) {
            // Try to infer type from input parameter
            const inputParam = findParameterByPath(inputSchema.parameters, mapping.inputKey);
            const dataType = inputParam?.dataTypes[0] || 'string';
            derivedParameters.push(createDerivedParameter(mapping.outputKey, dataType, `Mapped from ${mapping.inputKey}`));
            processedPaths.add(mapping.outputKey);
          }
          break;

        case 'expression':
          const expression = transform.content as PlaybookExpression;
          if (expression.declaredOutputProperties) {
            expression.declaredOutputProperties.forEach((prop: OutputProperty) => {
              if (!processedPaths.has(prop.path)) {
                derivedParameters.push(createDerivedParameter(prop.path, prop.dataType, prop.description || 'Expression output'));
                processedPaths.add(prop.path);
              }
            });
          }
          break;

        case 'spreading':
          const spreading = transform.content as PlaybookSpreading;
          if (spreading.inferredOutputProperties) {
            spreading.inferredOutputProperties.forEach((prop: OutputProperty) => {
              if (!processedPaths.has(prop.path)) {
                derivedParameters.push(createDerivedParameter(prop.path, prop.dataType, prop.description || 'Spread property'));
                processedPaths.add(prop.path);
              }
            });
          }
          break;
      }
    });

    // Create derived schema
    const derivedSchema: UnifiedSchema = {
      id: generateId(),
      name: `${playbook.name} - Derived Output Schema`,
      codeName: `${playbook.name.toLowerCase().replace(/\s+/g, '_')}_output`,
      description: `Automatically derived output schema for playbook: ${playbook.name}`,
      events: [], // Will be populated based on event configs
      parameters: derivedParameters,
      status: 'draft',
      type: SchemaType.PipelineSchema
    };

    return derivedSchema;
  } catch (error) {
    console.error('Error deriving output schema:', error);
    throw new Error(`Failed to derive output schema: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

/**
 * Compare derived schema with required schema
 */
export function comparePlaybookSchemas(derived: UnifiedSchema, required: UnifiedSchema): SchemaComparisonResult {
  const result: SchemaComparisonResult = {
    missing: [],
    extra: [],
    typeMismatches: []
  };

  // Create maps for efficient lookup
  const derivedMap = new Map(derived.parameters.map(p => [p.fullPath, p]));
  const requiredMap = new Map(required.parameters.map(p => [p.fullPath, p]));

  // Find missing parameters (in required but not in derived)
  required.parameters.forEach(requiredParam => {
    if (!derivedMap.has(requiredParam.fullPath)) {
      result.missing.push({
        path: requiredParam.fullPath,
        type: requiredParam.dataTypes[0] || 'unknown'
      });
    }
  });

  // Find extra parameters (in derived but not in required)
  derived.parameters.forEach(derivedParam => {
    if (!requiredMap.has(derivedParam.fullPath)) {
      result.extra.push({
        path: derivedParam.fullPath,
        type: derivedParam.dataTypes[0] || 'unknown'
      });
    }
  });

  // Find type mismatches
  derived.parameters.forEach(derivedParam => {
    const requiredParam = requiredMap.get(derivedParam.fullPath);
    if (requiredParam) {
      const derivedType = derivedParam.dataTypes[0];
      const requiredType = requiredParam.dataTypes[0];
      
      if (derivedType !== requiredType) {
        result.typeMismatches.push({
          path: derivedParam.fullPath,
          expected: requiredType,
          actual: derivedType
        });
      }
    }
  });

  return result;
}

// Helper functions

function parseTransformsFromYamlSection(
  sectionData: any, 
  scope: 'global' | 'eventSpecific', 
  eventIds?: string[]
): PlaybookTransformItem[] {
  const transforms: PlaybookTransformItem[] = [];

  // Parse enrichments
  if (sectionData.enrichments) {
    sectionData.enrichments.forEach((enrichment: any) => {
      let enrichmentType: 'DirectValue' | 'Timestamp' = 'DirectValue';
      let enrichmentValue: string | undefined = enrichment.staticString;

      if (enrichment.timestamp === 'seconds') {
        enrichmentType = 'Timestamp';
        enrichmentValue = 'seconds';
      }

      transforms.push({
        id: generateId(),
        type: 'enrichment',
        content: {
          type: enrichmentType,
          outputKey: enrichment.outputKey,
          value: enrichmentValue
        } as PlaybookEnrichment,
        appliesToScope: scope,
        eventIds: eventIds,
        outputPath: enrichment.outputKey
      });
    });
  }

  // Parse mappings - promote to expressions if applicable
  if (sectionData.mappings) {
    sectionData.mappings.forEach((mapping: any, index: number) => {
      const parsedTransforms = parseMappingTransforms(mapping.transforms);
      const isSingleExpressionTransform = 
        parsedTransforms && 
        parsedTransforms.length === 1 && 
        parsedTransforms[0].type === 'expression';

      // Check if it's an enrichment disguised as a mapping
      const isEnrichmentDisguisedAsMapping = 
        mapping.outputKey !== undefined && 
        mapping.value !== undefined && // Has a value property
        mapping.inputKey === undefined && // No input key
        (!mapping.transforms || mapping.transforms.length === 0); // No transforms array or empty

      if (isEnrichmentDisguisedAsMapping) {
        transforms.push({
          id: generateId(),
          type: 'enrichment', // Reclassify as enrichment
          content: {
            type: mapping.staticString ? 'DirectValue' : 'Timestamp', // Assuming 'value' maps to 'staticString' for type determination
            outputKey: mapping.outputKey,
            value: mapping.value // Use 'value' from the YAML
          } as PlaybookEnrichment,
          appliesToScope: scope,
          eventIds: eventIds,
          outputPath: mapping.outputKey
        });
      } else if (isSingleExpressionTransform) {
        // Promote to expression transform
        const expressionContent = parsedTransforms[0];
        const declaredOutputProperties: OutputProperty[] = [];

        // If outputKey is present, add it to declaredOutputProperties
        if (mapping.outputKey) {
          declaredOutputProperties.push({
            path: mapping.outputKey,
            dataType: 'string', // Default to string, can be refined later if schema inference is more advanced
            description: `Output from expression for ${mapping.outputKey}`
          });
        }

        transforms.push({
          id: generateId(),
          type: 'expression',
          name: mapping.name || `Expression ${index + 1}`, // Use mapping name or default
          content: {
            lang: expressionContent.lang,
            body: expressionContent.body,
            declaredOutputProperties: declaredOutputProperties.length > 0 ? declaredOutputProperties : undefined
          } as PlaybookExpression,
          appliesToScope: scope,
          eventIds: eventIds,
          inputPath: mapping.inputKey, // Preserve inputPath
          outputPath: mapping.outputKey // Preserve outputPath
        });
      } else {
        // Create regular mapping transform
        transforms.push({
          id: generateId(),
          type: 'mapping',
          content: {
            inputKey: mapping.inputKey,
            outputKey: mapping.outputKey,
            transforms: parsedTransforms, // Use the already parsed transforms
            defaultString: mapping.defaultString,
            defaultInt: mapping.defaultInt,
          } as PlaybookMapping,
          appliesToScope: scope,
          eventIds: eventIds,
          inputPath: mapping.inputKey, // Populate inputPath
          outputPath: mapping.outputKey // Populate outputPath
        });
      }
    });
  }

  // Parse expressions
  if (sectionData.expressions) {
    sectionData.expressions.forEach((expression: any, index: number) => {
      transforms.push({
        id: generateId(),
        type: 'expression',
        name: expression.name || `Expression ${index + 1}`, // Name can be derived or default
        // description is not needed
        content: {
          lang: expression.lang,
          body: expression.body,
          declaredOutputProperties: []
        } as PlaybookExpression,
        appliesToScope: scope,
        eventIds: eventIds
      });
    });
  }

  // Parse spreading
  if (sectionData.spreading) {
    sectionData.spreading.forEach((spreading: any, index: number) => {
      transforms.push({
        id: generateId(),
        type: 'spreading',
        name: spreading.name || `Spreading ${index + 1}`, // Name can be derived or default
        // description is not needed
        content: {
          inputKey: spreading.inputKey,
          prefix: spreading.prefix,
          separator: spreading.separator,
          depth: spreading.depth,
          arrayStartIndex: spreading.arrayStartIndex,
          inferredOutputProperties: [] // This will be inferred later or from UI
        } as PlaybookSpreading,
        appliesToScope: scope,
        eventIds: eventIds,
        inputPath: spreading.inputKey // Populate inputPath
      });
    });
  }

  // Parse filters
  if (sectionData.filters) {
    sectionData.filters.forEach((filter: any, index: number) => {
      let rulesContent: any = {};
      if (filter.byEventNames) {
        rulesContent.byEventNames = filter.byEventNames;
      }
      // Add other filter types here if they exist in the YAML spec
      // e.g., if (filter.byProperty) { rulesContent.byProperty = filter.byProperty; }

      transforms.push({
        id: generateId(),
        type: 'filter',
        name: filter.name || `Filter ${index + 1}`, // Name can be derived or default
        // description is not needed
        content: {
          rules: rulesContent
        } as PlaybookFilter,
        appliesToScope: scope,
        eventIds: eventIds
      });
    });
  }

  return transforms;
}

function buildYamlSection(transforms: PlaybookTransformItem[]): any {
  const section: any = {};

  // Group transforms by type
  const enrichments = transforms.filter(t => t.type === 'enrichment');
  const mappings = transforms.filter(t => t.type === 'mapping');
  const expressions = transforms.filter(t => t.type === 'expression');
  const spreadings = transforms.filter(t => t.type === 'spreading');
  const filters = transforms.filter(t => t.type === 'filter');

  // Sort transforms by their original YAML metadata to preserve order
  const sortByOriginalOrder = (a: PlaybookTransformItem, b: PlaybookTransformItem) => {
    const aIndex = (a as any).__yamlMetadata?.index ?? 999;
    const bIndex = (b as any).__yamlMetadata?.index ?? 999;
    return aIndex - bIndex;
  };

  // Only add sections that have transforms, and preserve original order
  if (mappings.length > 0) {
    section.mappings = mappings.sort(sortByOriginalOrder).map(t => {
      const content = t.content as PlaybookMapping;
      const mapping: any = {
        inputKey: content.inputKey,
        outputKey: content.outputKey
      };
      const builtTransforms = buildMappingTransformsForYaml(content.transforms);
      if (builtTransforms !== undefined && builtTransforms.length > 0) {
        mapping.transforms = builtTransforms;
      }
      if (content.defaultString !== undefined) {
        mapping.defaultString = content.defaultString;
      }
      if ((content as any).defaultInt !== undefined) {
        mapping.defaultInt = (content as any).defaultInt;
      }
      return mapping;
    });
  }

  if (spreadings.length > 0) {
    section.spreadings = spreadings.sort(sortByOriginalOrder).map(t => {
      const content = t.content as PlaybookSpreading;
      return {
        inputKey: content.inputKey,
        prefix: content.prefix,
        separator: content.separator,
        depth: content.depth,
        arrayStartIndex: content.arrayStartIndex
      };
    });
  }

  if (enrichments.length > 0) {
    section.enrichments = enrichments.sort(sortByOriginalOrder).map(t => {
      const content = t.content as PlaybookEnrichment;
      if (content.type === 'Timestamp' && content.value === 'seconds') {
        return {
          outputKey: content.outputKey,
          timestamp: 'seconds'
        };
      }
      return {
        outputKey: content.outputKey,
        staticString: content.value
      };
    });
  }

  if (expressions.length > 0) {
    section.expressions = expressions.sort(sortByOriginalOrder).map(t => {
      const content = t.content as PlaybookExpression;
      return {
        lang: content.lang,
        body: content.body
      };
    });
  }

  if (filters.length > 0) {
    section.filters = filters.sort(sortByOriginalOrder).map(t => {
      const content = t.content as PlaybookFilter;
      // Directly return the rules content without an extra 'rules' wrapper
      return content.rules;
    });
  }

  return section;
}

function parseMappingTransforms(transforms: any[] | undefined): any[] | undefined { // Allow undefined return
  if (!transforms || transforms.length === 0) {
    // If no transforms are specified in YAML, return undefined or empty array to match original spec
    return undefined; // Or [] if empty array is preferred for no transforms
  }

  const parsed = transforms.map(transform => {
    if (transform.expression) {
      return {
        type: 'expression',
        lang: transform.expression.lang,
        body: transform.expression.body,
      };
    }
    if (transform.modifyString) {
      return {
        type: 'modifyString',
        operation: transform.modifyString,
      };
    }
    // Potentially handle other sub-transform types here
    return transform;
  }).filter(Boolean); // Filter out any null/undefined results from map

  // If after parsing, the array is empty (e.g., only unrecognized transforms were present),
  // return undefined to match original spec.
  if (parsed.length === 0) {
    return undefined; // Or []
  }

  return parsed;
}

function buildMappingTransformsForYaml(transforms: any[] | undefined): any[] | undefined { // Change return type
  if (!transforms) {
    return undefined; // Return undefined instead of []
  }

  const built = transforms.map(transform => {
    if (transform.type === 'expression') {
      return {
        expression: {
          lang: transform.lang,
          body: transform.body,
        },
      };
    }
    if (transform.type === 'modifyString') {
      return {
        modifyString: transform.operation,
      };
    }
    return transform;
  }).filter(Boolean); // Filter out any null/undefined results

  if (built.length === 0) {
    return undefined; // If built array is empty, return undefined
  }

  return built;
}

function createDerivedParameter(path: string, dataType: string, description: string): Parameter {
  const pathParts = path.split('.');
  const codeName = pathParts[pathParts.length - 1];

  return {
    id: generateId(),
    path: path,
    codeName: codeName,
    friendlyName: codeName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
    description: description,
    dataTypes: [dataType as any],
    priorities: ['Standard'],
    regexFormula: '',
    scope: 'global',
    fullPath: path,
    isNew: true
  };
}

function findParameterByPath(parameters: Parameter[], path: string): Parameter | undefined {
  return parameters.find(p => p.fullPath === path || p.path === path);
}

/**
 * Creates a unique signature for a transform based on its type and content.
 * This is used to identify identical transforms across different events.
 */
function createTransformSignature(transformType: string, transformData: any): string {
  // For each transform type, create a signature based on its key properties
  switch (transformType) {
    case 'enrichment':
    case 'enrichments':
      return `enrichment:${transformData.outputKey}:${transformData.staticString || ''}`;
      
    case 'mapping':
    case 'mappings':
      const transformsSignature = Array.isArray(transformData.transforms) 
        ? transformData.transforms.map((t: any) => JSON.stringify(t)).join('|')
        : '';
      return `mapping:${transformData.inputKey}:${transformData.outputKey}:${transformsSignature}:${transformData.defaultString || ''}:${transformData.defaultInt || ''}`;
      
    case 'expression':
    case 'expressions':
      return `expression:${transformData.lang || ''}:${transformData.body || ''}`;
      
    case 'spreading': // Keep singular for internal type
    case 'spreadings': // Handle plural for YAML key
      return `spreading:${transformData.inputKey || ''}:${transformData.prefix || ''}:${transformData.separator || ''}:${transformData.depth || ''}:${transformData.arrayStartIndex || ''}`;
      
    case 'filter':
    case 'filters':
      return `filter:${JSON.stringify(transformData.rules || [])}`;
      
    default:
      // For unknown types, stringify the entire object
      return `${transformType}:${JSON.stringify(transformData)}`;
  }
}
